import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { examAPI, WorksheetProgressQuestionItem, WorksheetProgressRow } from '../services/api';
import { Badge, EmptyState, IconBadge, SegmentedControl, Skeleton } from './ui';
import {
  activeDotHalo,
  AreaGradient,
  chartAxis,
  chartCursorLine,
  chartGrid,
  chartXAxis,
  seriesAnimation,
  tooltipWrapper,
} from '../theme/chartTheme';

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';
const FETCH_PAGE_SIZE = 500;
const MAX_PAGES = 10;
const DAY_MS = 24 * 60 * 60 * 1000;
const DAYS_SHOWN = 7;
const WEEKS_SHOWN = 12;

const C = {
  card: '#FFFFFF',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  grid: '#EEF2F7',
  text: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  purple: '#4F46E5',
  purpleSoft: 'rgba(124,58,237,0.08)',
  green: '#10B981',
  greenSoft: 'rgba(16,185,129,0.10)',
  amber: '#F59E0B',
  amberSoft: 'rgba(245,158,11,0.10)',
  red: '#EF4444',
  redSoft: 'rgba(239,68,68,0.10)',
  shadow: '0 3px 12px rgba(15,23,42,0.05)',
};

type ViewMode = 'week' | 'day';

interface QuestionEvent {
  worksheetId: number;
  worksheetName: string;
  assignedAt: number;
  solvedAt: number | null;
}

interface ProgressPoint {
  label: string;
  tooltipLabel: string;
  completion: number | null;
  solved: number;
  assigned: number;
  worksheets: number;
  /** Completion of each worksheet at the end of the period (for the tooltip). */
  breakdown: { name: string; pct: number }[];
}

interface WorksheetWeeklyProgressChartProps {
  studentId: number;
  studentName: string;
  className: string;
  section: string;
  subject?: string;
  refreshNonce: number;
  /** When provided, these rows are used instead of fetching from the API. */
  rowsOverride?: WorksheetProgressRow[];
}

const toTime = (value?: string | null) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const startOfDay = (time: number) => {
  const date = new Date(time);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

// Monday 00:00 local time of the week containing `time`.
const startOfWeek = (time: number) => {
  const date = new Date(startOfDay(time));
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return date.getTime();
};

const shiftDays = (time: number, days: number) => {
  const date = new Date(time);
  date.setDate(date.getDate() + days);
  return date.getTime();
};

const formatShort = (time: number) => new Date(time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

const toEvents = (rows: WorksheetProgressRow[]): QuestionEvent[] => rows.flatMap((row) => (row.progress ?? []).flatMap((item: WorksheetProgressQuestionItem) => {
  const submittedAt = toTime(item.last_submitted_at);
  const assignedAt = toTime(item.created_at) ?? toTime(row.exam?.exam_date) ?? submittedAt;
  if (assignedAt == null) return [];
  return [{ worksheetId: row.student_result_id, worksheetName: row.exam?.name || `Worksheet #${row.student_result_id}`, assignedAt, solvedAt: item.status === 'solved' ? submittedAt ?? assignedAt : null }];
}));

// Completion % at the end of each day/week: questions solved so far / questions assigned so far.
const buildPoints = (events: QuestionEvent[], view: ViewMode): ProgressPoint[] => {
  const today = startOfDay(Date.now());
  const periodStarts = view === 'day'
    ? Array.from({ length: DAYS_SHOWN }, (_, i) => shiftDays(today, i - (DAYS_SHOWN - 1)))
    : Array.from({ length: WEEKS_SHOWN }, (_, i) => shiftDays(startOfWeek(today), 7 * (i - (WEEKS_SHOWN - 1))));

  return periodStarts.map((periodStart) => {
    const periodEnd = shiftDays(periodStart, view === 'day' ? 1 : 7);
    const assignedEvents = events.filter((event) => event.assignedAt < periodEnd);
    const assigned = assignedEvents.length;
    const worksheets = new Set(assignedEvents.map((event) => event.worksheetId)).size;
    const solved = events.filter((event) => event.solvedAt != null && event.solvedAt < periodEnd).length;

    const perWorksheet = new Map<number, { name: string; assigned: number; solved: number }>();
    assignedEvents.forEach((event) => {
      const entry = perWorksheet.get(event.worksheetId) ?? { name: event.worksheetName, assigned: 0, solved: 0 };
      entry.assigned += 1;
      if (event.solvedAt != null && event.solvedAt < periodEnd) entry.solved += 1;
      perWorksheet.set(event.worksheetId, entry);
    });
    const breakdown = Array.from(perWorksheet.values()).map((w) => ({ name: w.name, pct: Math.round((w.solved / w.assigned) * 100) }));

    return {
      label: view === 'day'
        ? new Date(periodStart).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit' })
        : formatShort(periodStart),
      tooltipLabel: view === 'day'
        ? new Date(periodStart).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short' })
        : `Week of ${formatShort(periodStart)} – ${formatShort(periodEnd - DAY_MS)}`,
      completion: assigned > 0 ? Math.round((solved / assigned) * 100) : null,
      solved,
      assigned,
      worksheets,
      breakdown,
    };
  });
};

interface WorksheetSummaryRow {
  id: number;
  name: string;
  subject: string | null;
  assignedAt: number | null;
  total: number;
  solved: number;
  inProgress: number;
  notStarted: number;
  completion: number;
}

const toWorksheetRows = (rows: WorksheetProgressRow[]): WorksheetSummaryRow[] => rows
  .filter((row) => (row.summary?.total ?? 0) > 0)
  .map((row) => {
    const assignedTimes = (row.progress ?? [])
      .map((item) => toTime(item.created_at))
      .filter((time): time is number => time != null);
    const total = row.summary.total;
    const solved = row.summary.solved ?? 0;
    const inProgress = row.summary.attempted ?? 0;
    return {
      id: row.student_result_id,
      name: row.exam?.name || `Exam #${row.exam?.id ?? row.student_result_id}`,
      subject: row.exam?.subject ?? null,
      assignedAt: assignedTimes.length ? Math.min(...assignedTimes) : toTime(row.exam?.exam_date),
      total,
      solved,
      inProgress,
      notStarted: Math.max(0, total - solved - inProgress),
      completion: Math.round((solved / total) * 100),
    };
  })
  .sort((a, b) => (b.assignedAt ?? 0) - (a.assignedAt ?? 0));

const CountPill: React.FC<{ value: number; tone: 'success' | 'warning' | 'danger' }> = ({ value, tone }) => (
  <Badge tone={value > 0 ? tone : 'neutral'} className="sl-num" style={{ minWidth: 34, justifyContent: 'center' }}>{value}</Badge>
);

const VIEW_OPTIONS = [
  { key: 'week', label: 'Week wise' },
  { key: 'day', label: 'Day wise' },
] as const;

const WorksheetBreakdown: React.FC<{ worksheets: WorksheetSummaryRow[] }> = ({ worksheets }) => {
  const totals = worksheets.reduce((acc, ws) => ({
    questions: acc.questions + ws.total,
    solved: acc.solved + ws.solved,
    pending: acc.pending + ws.total - ws.solved,
  }), { questions: 0, solved: 0, pending: 0 });

  return (
    <div style={{ marginTop: 24, borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <IconBadge tone="brand" size={32}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeLinejoin="round" /><path d="M14 3v5h5M9 13h6M9 17h4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </IconBadge>
          <h3 className="sl-card__title" style={{ fontSize: 15 }}>Assigned worksheets</h3>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          <Badge tone="neutral"><span className="sl-num">{worksheets.length}</span> worksheet{worksheets.length === 1 ? '' : 's'}</Badge>
          <Badge tone="neutral"><span className="sl-num">{totals.questions}</span> questions</Badge>
          <Badge tone="success" dot><span className="sl-num">{totals.solved}</span> solved</Badge>
          <Badge tone={totals.pending > 0 ? 'danger' : 'neutral'} dot><span className="sl-num">{totals.pending}</span> pending</Badge>
        </div>
      </div>
      <div className="sl-table-wrap">
        <table className="sl-table" style={{ minWidth: 680 }} aria-label="Assigned worksheets">
          <thead>
            <tr>
              <th>Worksheet</th>
              <th>Assigned</th>
              <th className="is-center">Questions</th>
              <th className="is-center">Solved</th>
              <th className="is-center">In progress</th>
              <th className="is-center">Not started</th>
              <th style={{ width: 180 }}>Completion</th>
            </tr>
          </thead>
          <tbody>
            {worksheets.map((ws) => (
              <tr key={ws.id}>
                <td>
                  <div style={{ fontWeight: 700 }}>{ws.name}</div>
                  {ws.subject ? <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{ws.subject}</div> : null}
                </td>
                <td style={{ color: C.textSecondary, whiteSpace: 'nowrap' }}>{ws.assignedAt ? formatShort(ws.assignedAt) : '—'}</td>
                <td className="is-center sl-num" style={{ fontWeight: 700 }}>{ws.total}</td>
                <td className="is-center"><CountPill value={ws.solved} tone="success" /></td>
                <td className="is-center"><CountPill value={ws.inProgress} tone="warning" /></td>
                <td className="is-center"><CountPill value={ws.notStarted} tone="danger" /></td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      role="progressbar"
                      aria-label={`${ws.name} completion`}
                      aria-valuenow={ws.completion}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      style={{ flex: 1, height: 8, borderRadius: 999, background: '#EEF1F6', overflow: 'hidden' }}
                    >
                      <div className="sl-grow-x" style={{ width: `${ws.completion}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #818CF8, #4F46E5)' }} />
                    </div>
                    <span className="sl-num" style={{ fontSize: 12.5, fontWeight: 700, color: C.text, minWidth: 38, textAlign: 'right' }}>{ws.completion}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ProgressTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as ProgressPoint;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.96)', border: '1px solid #E3E7EF', borderRadius: 12, padding: '10px 14px',
      boxShadow: '0 4px 8px rgba(16,24,40,0.04), 0 24px 48px -12px rgba(16,24,40,0.20)', fontFamily: FONT,
      backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', fontVariantNumeric: 'tabular-nums', minWidth: 190,
    }}>
      <div className="sl-eyebrow" style={{ fontSize: 10.5, marginBottom: 6 }}>{point.tooltipLabel}</div>
      {point.completion == null ? (
        <div style={{ fontSize: 13, color: C.textMuted }}>No worksheets assigned yet</div>
      ) : (
        <>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.purple, letterSpacing: '-0.02em' }}>{point.completion}% overall</div>
          {point.breakdown.length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #EEF1F6', display: 'grid', gap: 5 }}>
              {point.breakdown.map((w) => (
                <div key={w.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, fontSize: 12 }}>
                  <span style={{ color: C.textSecondary, fontWeight: 600, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
                  <span style={{ fontWeight: 800, color: C.text }}>{w.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const WorksheetWeeklyProgressChart: React.FC<WorksheetWeeklyProgressChartProps> = ({
  studentId,
  studentName,
  className,
  section,
  subject,
  refreshNonce,
  rowsOverride,
}) => {
  const [rows, setRows] = useState<WorksheetProgressRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState<ViewMode>('week');

  useEffect(() => {
    let isCancelled = false;

    const run = async () => {
      if (rowsOverride) {
        setError('');
        setLoading(false);
        setRows(rowsOverride);
        return;
      }
      if (!className || !section) {
        setRows([]);
        setError('Class and section are not available for this student.');
        return;
      }

      try {
        setLoading(true);
        setError('');
        const collected: WorksheetProgressRow[] = [];
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const data = await examAPI.getWorksheetProgress({
            class_name: className,
            section,
            subject: subject || undefined,
            student_id: studentId,
            include_progress: true,
            page,
            page_size: FETCH_PAGE_SIZE,
          });
          collected.push(...(data.rows ?? []));
          if (collected.length >= data.total_rows || (data.rows ?? []).length === 0) break;
        }
        if (!isCancelled) setRows(collected);
      } catch (err: any) {
        if (!isCancelled) {
          setRows([]);
          setError(err?.response?.data?.detail || err?.message || 'Failed to load progress.');
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    run();
    return () => {
      isCancelled = true;
    };
  }, [studentId, className, section, subject, refreshNonce, rowsOverride]);

  const events = useMemo(() => toEvents(rows), [rows]);
  const points = useMemo(() => buildPoints(events, view), [events, view]);
  const worksheets = useMemo(() => toWorksheetRows(rows), [rows]);
  const withData = points.filter((point) => point.completion != null);
  const latest = withData[withData.length - 1];
  const change = latest && withData.length > 1 ? (latest.completion ?? 0) - (withData[0].completion ?? 0) : null;
  const periodLabel = view === 'day' ? `last ${DAYS_SHOWN} days` : `last ${WEEKS_SHOWN} weeks`;

  return (
    <div className="sl-card sl-anim-in" style={{ padding: '22px 24px 24px', fontFamily: FONT }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
          <IconBadge tone="brand" size={36}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19V5M4 19h16M8 15l3-4 3 2 4-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </IconBadge>
          <div style={{ minWidth: 0 }}>
            <h3 className="sl-card__title">Worksheet completion</h3>
            <div className="sl-card__sub">
              {studentName} · {periodLabel}{subject ? ` · ${subject}` : ''}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          {latest ? (
            <div style={{ textAlign: 'right' }}>
              <div className="sl-num" style={{ fontSize: 28, fontWeight: 800, color: C.text, lineHeight: 1 }}>{latest.completion}%</div>
              <div style={{ marginTop: 6 }}>
                {change ? (
                  <span className={`sl-trend ${change > 0 ? 'is-up' : 'is-down'}`}>
                    <span aria-hidden>{change > 0 ? '▲' : '▼'}</span> {Math.abs(change)}% in {periodLabel}
                  </span>
                ) : (
                  <span style={{ fontSize: 11.5, color: C.textMuted, fontWeight: 600 }}>completed</span>
                )}
              </div>
            </div>
          ) : null}

          <SegmentedControl
            ariaLabel="Completion period"
            size="sm"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 10 }} aria-busy="true" aria-label="Loading progress">
          <Skeleton height={220} radius={12} />
          <Skeleton height={12} width="40%" />
        </div>
      ) : error ? (
        <EmptyState compact tone="danger" icon="error" title="Couldn't load progress" body={error} />
      ) : withData.length === 0 ? (
        <EmptyState compact icon="chart" title="No worksheet activity yet" body={`No worksheets assigned to this student in the ${periodLabel}.`} />
      ) : (
        <div
          role="img"
          aria-label={`Worksheet completion for ${studentName}, ${periodLabel}. Latest ${latest?.completion ?? 0}% complete.`}
          style={{ width: '100%', height: 280 }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 12, right: 12, bottom: 4, left: 4 }}>
              <AreaGradient id="ws-completion-fill" color={C.purple} from={0.24} to={0} />
              <CartesianGrid {...chartGrid} />
              <XAxis {...chartXAxis} dataKey="label" padding={{ left: 16, right: 16 }} interval="preserveStartEnd" minTickGap={12} />
              <YAxis {...chartAxis} width={44} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(value) => `${value}%`} />
              <Tooltip {...tooltipWrapper} content={<ProgressTooltip />} cursor={chartCursorLine} />
              <Area
                type="monotone"
                dataKey="completion"
                stroke="none"
                fill="url(#ws-completion-fill)"
                tooltipType="none"
                activeDot={false}
                {...seriesAnimation()}
              />
              <Line
                type="monotone"
                dataKey="completion"
                stroke={C.purple}
                strokeWidth={2.75}
                strokeLinecap="round"
                dot={{ r: 3.5, fill: '#FFFFFF', stroke: C.purple, strokeWidth: 2 }}
                activeDot={activeDotHalo(C.purple)}
                {...seriesAnimation()}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 12, lineHeight: 1.55 }}>
        Completion = questions solved so far ÷ questions assigned so far, at the end of each {view === 'day' ? 'day' : 'week'}.
      </div>

      {!loading && !error && worksheets.length > 0 ? <WorksheetBreakdown worksheets={worksheets} /> : null}
    </div>
  );
};

export default WorksheetWeeklyProgressChart;
